import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import type { HttpClient, RequestOptions } from '~/shared/api/http-client'
import { ErrorApi } from '~/shared/api'
import type { Notifier } from '~/shared/interfaces'

export type { RequestOptions } from '~/shared/api/http-client'

export const ERROR_NOT_FOUND = {
  HTTP_CODE: 404,
  MESSAGE: 'Not Found',
} as const

export const isErrorNotFoundResponse = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error) && error.response?.status === ERROR_NOT_FOUND.HTTP_CODE

export class RequestService implements HttpClient {
  constructor(
    private readonly axios: AxiosInstance,
    private readonly notifier: Notifier,
  ) {}

  public async request<T = unknown, D = unknown>(
    options: RequestOptions<D>,
  ): Promise<AxiosResponse<T>> {
    const { silent, ...requestOptions } = options

    const opts: AxiosRequestConfig<D> = {
      ...requestOptions,
      method: requestOptions.method ?? 'GET',
      params: requestOptions.params ?? {},
    }

    try {
      return await this.axios.request<T, AxiosResponse<T>, D>(opts)
    }
    catch (err: unknown) {
      if (isErrorNotFoundResponse(err)) {
        throw new ErrorApi(new Error(ERROR_NOT_FOUND.MESSAGE), { cause: err })
      }

      if (!silent) {
        this.notifier.showError('Что-то пошло не так. Попробуйте перезагрузить страницу')
      }

      throw new ErrorApi(err instanceof Error ? err : new Error('Request failed'))
    }
  }
}
